/**
 * Requests to the Morcast API.
 *
 * The API is served from the same origin as the app, so every request carries the session cookie
 * without extra code. A failed request throws `ApiRequestError` with the API's stable error code,
 * which the UI can react to, and its human-readable message, which the UI can show.
 */

/** A failed API request. */
export class ApiRequestError extends Error {
  constructor(
    /** HTTP status. 0 when the API could not be reached at all. */
    readonly status: number,
    /** The API's error code, e.g. `INVALID_NONCE`. */
    readonly code: string,
    message: string,
    /** Extra facts some errors carry, e.g. `{ problems: [...] }`. */
    readonly details: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = "ApiRequestError";
  }

  /** The list of problems in `details`, for errors that report several. */
  get problems(): string[] {
    const problems = this.details.problems;
    return Array.isArray(problems) ? problems.map(String) : [];
  }
}

/** The JSON body of an API error answer. */
interface ErrorBody {
  code?: string;
  message?: string;
  details?: Record<string, unknown>;
}

/**
 * Sends a request and returns the parsed JSON answer.
 *
 * @param body Sent as JSON when given. Requests without a body send no content type, because the
 *             API rejects an empty body declared as JSON.
 * @throws ApiRequestError when the API cannot be reached or answers with an error status.
 */
export async function apiRequest<T>(
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
  path: string,
  body?: unknown,
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, {
      method,
      credentials: "same-origin",
      // A file goes as it is, with its own type. Anything else as JSON.
      ...(body === undefined
        ? {}
        : body instanceof Blob
          ? { headers: { "content-type": body.type }, body }
          : { headers: { "content-type": "application/json" }, body: JSON.stringify(body) }),
    });
  } catch {
    throw new ApiRequestError(
      0,
      "NETWORK_ERROR",
      "Morcast cannot be reached. Check your connection and try again.",
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }
  const data: unknown = await response.json().catch(() => undefined);
  if (!response.ok) {
    const error = (data ?? {}) as ErrorBody;
    throw new ApiRequestError(
      response.status,
      error.code ?? "UNKNOWN_ERROR",
      error.message ?? `The request failed (status ${response.status}).`,
      error.details ?? {},
    );
  }
  return data as T;
}

/** Shorthands for the methods the app uses. */
export const api = {
  get: <T>(path: string) => apiRequest<T>("GET", path),
  post: <T>(path: string, body?: unknown) => apiRequest<T>("POST", path, body),
  put: <T>(path: string, body: unknown) => apiRequest<T>("PUT", path, body),
  patch: <T>(path: string, body: unknown) => apiRequest<T>("PATCH", path, body),
  delete: <T>(path: string) => apiRequest<T>("DELETE", path),
  /** Sends a file as the request body, with its own type. */
  upload: <T>(path: string, file: Blob) => apiRequest<T>("POST", path, file),
};
